// Denied & underpaid fixtures (APPEALS_HANDOFF §12). Copy is from the boards.
import type { AppealKind, AppealSent, AppealSettings, Claim, Deadline, Letter, PacketDoc } from './types';
import { CHANNELS_IN_ORDER, PAYER_PLAN_RULES } from './payerRules';
import type { Channel } from './payerRules';
import { PMS } from '@/features/shared/pms';

export function buildDenied(): Claim[] {
  return [
    // CLM-0818 — Linda Okafor · Appeal (board 1)
    {
      id: 'CLM-0818',
      kind: 'denied',
      patient: 'Linda Okafor',
      firstName: 'Linda',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      memberId: 'W281-30-5521',
      // In-network necessity denial: the Aetna contract doesn't allow billing Linda (D157, D212).
      contractDenial: 'necessity',
      serviceDate: 'Aug 18, 2026',
      provider: 'Dr. Emily Carter',
      procedures: [
        { code: 'D2740', description: 'Crown, porcelain/ceramic', tooth: '#3', fee: 1295 },
        { code: 'D2950', description: 'Core build-up, including any pins', tooth: '#3', fee: 285 },
      ],
      deniedDate: 'Oct 1, 2026',
      answer: 'appeal',
      why: {
        eyebrow: 'Why Aetna denied it',
        headline: 'Aetna says the crown and build-up weren’t necessary',
        metaLine: 'Denial code CARC 50 — not deemed a medical necessity · from Aetna’s payment of Oct 1',
        sources: [{ id: 'eob-0818', label: 'Aetna EOB, Oct 1' }],
      },
      deadline: { date: '2027-03-30', basis: 'Aetna allows 180 days from its decision', verb: 'Appeal' },
      appealKind: 'clinical',
      approval: 'approved',
      approvedBy: 'Dr. Carter',
      approvedOn: 'Oct 14',
      recommendationHeader: 'ELVA recommends an appeal',
      recommendationBody:
        'The evidence supports the crown. The X-ray of Aug 18 shows decay under more than half of the tooth, and Dr. Carter’s note records a fractured cusp. This year Aetna paid 6 of the 7 crown denials your office appealed with an X-ray and a narrative.',
      recommendationSources: [
        { id: 'xray-0818', label: 'X-ray, Aug 18' },
        { id: 'note-0818', label: 'Clinical note, Aug 18' },
        { id: 'ev-0818-appeals', label: '7 Aetna crown appeals' },
        { id: 'ev-0818-rules', label: 'Aetna appeal rules' },
      ],
      packet: [
        { n: 1, name: 'Appeal letter', origin: 'Written by ELVA · Dr. Carter signs it', pages: 1, isLetter: true, needsSignature: true },
        { n: 2, name: 'Aetna Provider Complaint and Appeal Form', origin: 'Filled in by ELVA · Dr. Carter signs it', pages: 2, needsSignature: true },
        { n: 3, name: 'Aetna’s explanation of benefits, Oct 1', origin: 'From Aetna’s electronic payment', pages: 1 },
        { n: 4, name: 'The original claim', origin: 'ADA claim form, as sent on Aug 19', pages: 1 },
        { n: 5, name: 'Periapical X-ray, Aug 18', origin: `From ${PMS} imaging`, pages: 1 },
        { n: 6, name: 'Clinical note, Aug 18', origin: `From ${PMS}`, pages: 2 },
      ],
      letter: {
        text:
          'To the Appeals Team,\n\nWe are appealing Aetna’s decision of October 1, 2026, which denied the crown and core build-up on tooth #3 as not medically necessary (CARC 50).\n\nThe periapical X-ray of August 18, 2026 shows recurrent decay under the existing restoration, involving more than half of the coronal structure. Dr. Carter’s clinical note of the same day records a fractured mesiolingual cusp. A direct restoration cannot restore this tooth, and without full coverage it is at risk of fracturing to the root.\n\nThe X-ray, the clinical note and the original claim are enclosed. We ask Aetna to review its decision and pay the claim under Ms. Okafor’s plan.',
        basis: 'the X-ray, the clinical note and Aetna’s denial',
      },
      history: [
        'Aetna denied Oct 1 (electronic payment)',
        'ELVA prepared the appeal Oct 2',
      ],
    },

    // CLM-0827 — Michael Torres · Appeal (Cigna, unknown address)
    {
      id: 'CLM-0827',
      kind: 'denied',
      patient: 'Michael Torres',
      firstName: 'Michael',
      payer: { name: 'Cigna DPPO', short: 'Cigna' },
      memberId: 'U77-118-305',
      // Cigna's own record error, not a patient charge: the contract doesn't allow billing Michael (D157).
      canBillPatient: false,
      contractDenial: 'frequency',
      serviceDate: 'Aug 27, 2026',
      provider: 'Dr. Olivia Brooks',
      procedures: [{ code: 'D2740', description: 'Crown, porcelain/ceramic', tooth: '#30', fee: 1295 }],
      deniedDate: 'Oct 7, 2026',
      answer: 'appeal',
      appealKind: 'billing',
      why: {
        eyebrow: 'Why Cigna denied it',
        headline: 'Cigna says #30 had a crown in 2023. That crown was on #31.',
        metaLine: 'Denial code CARC 119 — benefit limit, crown replacement · from Cigna’s payment of Oct 7',
        sources: [{ id: 'eob-0827', label: 'Cigna EOB, Oct 7' }],
      },
      deadline: { date: '2027-04-05', basis: 'Cigna allows 180 days from its decision', verb: 'Appeal' },
      recommendationHeader: 'ELVA recommends an appeal',
      recommendationBody:
        'Cigna’s records have the wrong tooth. The periapical X-ray of Aug 27 shows tooth #30 with a large failing restoration and no prior crown, and the enclosed 2023 claim shows that crown was placed on tooth #31.',
      recommendationSources: [
        { id: 'xray-0827', label: 'X-ray, Aug 27' },
        { id: 'claim-2023', label: 'The 2023 claim' },
      ],
      packet: [
        { n: 1, name: 'Appeal letter', origin: 'Written by ELVA · Dr. Brooks signs it', pages: 1, isLetter: true, needsSignature: true },
        { n: 2, name: 'Cigna Request for Health Care Provider Payment Review', origin: 'Filled in by ELVA · Dr. Brooks signs it', pages: 2, needsSignature: true },
        { n: 3, name: 'Cigna’s explanation of benefits, Oct 7', origin: 'From Cigna’s electronic payment', pages: 1 },
        { n: 4, name: 'The original claim', origin: 'ADA claim form, as sent on Aug 28', pages: 1 },
        { n: 5, name: 'Periapical X-ray, Aug 27', origin: `From ${PMS} imaging`, pages: 1 },
        { n: 6, name: 'The 2023 claim, showing the crown on #31', origin: `From ${PMS} claim history`, pages: 1 },
      ],
      letter: {
        text:
          'To the Appeals Team,\n\nWe are appealing Cigna’s decision of October 7, 2026, which denied the crown on tooth #30 as exceeding the five-year replacement limit. Cigna’s records show a crown placed in 2023; that crown was placed on tooth #31, as the enclosed 2023 claim shows. Tooth #30 has not had a crown before. The periapical X-ray of August 27, 2026 shows tooth #30 with a large failing restoration and recurrent decay. We ask Cigna to correct the tooth on its 2023 record and pay this claim.',
        basis: 'the X-ray, the 2023 claim and Cigna’s denial',
      },
      history: ['Cigna denied Oct 7 (electronic payment)', 'ELVA prepared the appeal Oct 8'],
    },

    // CLM-0902 — Angela Ruiz · Resend (board 3)
    {
      id: 'CLM-0902',
      kind: 'denied',
      patient: 'Angela Ruiz',
      firstName: 'Angela',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      memberId: 'W281-44-2090',
      serviceDate: 'Sep 2, 2026',
      provider: 'Dr. Emily Carter',
      procedures: [
        { code: 'D4341', description: 'Scaling and root planing, 4+ teeth per quadrant', tooth: 'UR', fee: 265 },
        { code: 'D4341', description: 'Scaling and root planing, 4+ teeth per quadrant', tooth: 'UL', fee: 265 },
      ],
      deniedDate: 'Sep 24, 2026',
      answer: 'resend',
      why: {
        eyebrow: 'Why Aetna denied it',
        headline: 'Aetna didn’t get the pocket depths',
        metaLine: 'Denial code CARC 16 — the claim was missing information · from Aetna’s payment of Sep 24',
        sources: [{ id: 'eob-0902', label: 'Aetna EOB, Sep 24' }],
      },
      resendReason: 'Aetna didn’t get the pocket depths',
      resendSoft:
        'This isn’t a disagreement, so it doesn’t need an appeal. ELVA attached the periodontal chart from Sep 2 and will send the claim again electronically.',
      resendAttached: 'the periodontal chart from Sep 2',
      recommendationSources: [{ id: 'chart-0902', label: 'Periodontal chart, Sep 2' }],
      history: ['Aetna denied Sep 24 (electronic payment)', 'ELVA attached the periodontal chart Sep 25'],
    },

    // CLM-0908 — Peter Walsh · Bill the patient (board 3)
    {
      id: 'CLM-0908',
      kind: 'denied',
      patient: 'Peter Walsh',
      firstName: 'Peter',
      payer: { name: 'MetLife', short: 'MetLife' },
      memberId: '448-90-3311',
      serviceDate: 'Sep 8, 2026',
      provider: 'Dr. Olivia Brooks',
      procedures: [{ code: 'D7210', description: 'Surgical extraction, erupted tooth', tooth: '#17', fee: 395 }],
      deniedDate: 'Sep 29, 2026',
      answer: 'bill_patient',
      billAmount: 395,
      coverageDenial: true,
      why: {
        eyebrow: 'Why MetLife denied it',
        headline: 'MetLife is right: Peter’s coverage ended Aug 31',
        metaLine: 'Denial code CARC 27 — expenses incurred after coverage ended · from MetLife’s payment of Sep 29',
        sources: [{ id: 'eob-0908', label: 'MetLife EOB, Sep 29' }],
      },
      coverageResult: 'ELVA checked for other coverage — none found active on Sep 29.',
      barReason: 'The visit was after Peter’s coverage ended. ELVA found no other coverage on Sep 29.',
      billSoft:
        'The visit was on Sep 8, after Peter’s MetLife coverage ended. An appeal wouldn’t change this. ELVA already checked for other coverage and found none, so Peter owes it.',
      recommendationSources: [{ id: 'elig-0908', label: 'Eligibility check, Sep 29' }],
      history: ['MetLife denied Sep 29 (electronic payment)', 'ELVA checked eligibility Sep 29'],
    },

    // CLM-0915 — Sofia Martinez · Needs you (board 3)
    {
      id: 'CLM-0915',
      kind: 'denied',
      patient: 'Sofia Martinez',
      firstName: 'Sofia',
      payer: { name: 'Delta Dental PPO', short: 'Delta' },
      memberId: '0917-44-6120',
      // In-network frequency denial: the Delta contract doesn't allow billing Sofia (D212).
      contractDenial: 'frequency',
      serviceDate: 'Sep 15, 2026',
      provider: 'Dr. Olivia Brooks',
      procedures: [{ code: 'D1110', description: 'Prophylaxis, adult', tooth: null, fee: 183 }],
      deniedDate: 'Oct 6, 2026',
      answer: 'needs_you',
      why: {
        eyebrow: 'Why Delta denied it',
        headline: 'Delta says Sofia had a cleaning at another office on Jul 8',
        metaLine: 'Denial code CARC 119 — benefit frequency reached · from Delta’s payment of Oct 6',
        sources: [{ id: 'eob-0915', label: 'Delta EOB, Oct 6' }],
      },
      decision: {
        title: 'Delta says Sofia had a cleaning at another office on Jul 8.',
        body:
          'That would make this her third cleaning this year; her plan covers two. Her last cleaning here was Mar 12. ELVA can’t see other offices’ records, so only Sofia can say.',
        askQuestion: 'Did she have a cleaning at another dental office around Jul 8?',
        askPhone: '(210) 555-0147',
        options: [
          {
            id: 'didnt',
            label: 'She didn’t — appeal it',
            hint: 'ELVA prepares the appeal with her visit history here.',
            resolvesTo: 'appeal',
            recorded: 'Sofia said she had no cleaning elsewhere',
          },
          {
            id: 'did',
            label: 'She did — Delta is right',
            hint: 'Your network contract with Delta doesn’t allow billing her for a cleaning denied as over the frequency limit, and no waiver signed before the visit is on file. This is a write-off.',
            resolvesTo: 'write_off',
            recorded: 'Sofia said she did have a cleaning elsewhere',
          },
          {
            id: 'other',
            label: 'Something else',
            hint: 'Add a note.',
            resolvesTo: 'appeal',
            recorded: 'Something else',
            needsNote: true,
          },
        ],
      },
      recommendationSources: [{ id: 'visits-0915', label: 'Sofia’s visits here' }],
      history: ['Delta denied Oct 6 (electronic payment)', 'ELVA reviewed it Oct 7'],
    },
  ];
}

/** Sofia's short appeal packet + letter, applied when she is appealed. */
export const SOFIA_APPEAL: {
  deadline: Deadline;
  appealKind: AppealKind;
  recommendationHeader: string;
  recommendationBody: string;
  packet: PacketDoc[];
  letter: Letter;
} = {
  deadline: { date: '2027-04-04', basis: 'Delta allows 180 days from its decision', verb: 'Appeal' },
  appealKind: 'billing',
  recommendationHeader: 'ELVA recommends an appeal',
  recommendationBody:
    'Sofia says she hasn’t been seen elsewhere this year. Her only cleanings this benefit year were here, on Mar 12 and Sep 15, so this is her second, not her third.',
  packet: [
    { n: 1, name: 'Appeal letter', origin: 'Written by ELVA · Dr. Brooks signs it', pages: 1, isLetter: true, needsSignature: true },
    { n: 2, name: 'Delta Provider Inquiry Form', origin: 'Filled in by ELVA · Dr. Brooks signs it', pages: 1, needsSignature: true },
    { n: 3, name: 'Delta’s explanation of benefits, Oct 6', origin: 'From Delta’s electronic payment', pages: 1 },
    { n: 4, name: 'Sofia’s cleaning history here', origin: `From ${PMS}`, pages: 1 },
  ],
  letter: {
    text:
      'To the Appeals Team,\n\nWe are appealing Delta’s denial of October 6, 2026 for Ms. Martinez’s adult cleaning (D1110) on September 15, 2026, denied as exceeding the frequency limit. Delta’s records show a cleaning at another office on July 8, 2026. Ms. Martinez confirms she has not been seen elsewhere this year. Her only cleanings this benefit year were in our office, on March 12 and September 15. We ask Delta to correct its record and pay this claim.',
    basis: 'Sofia’s cleaning history and Delta’s denial',
  },
};

export function buildUnderpaid(): Claim[] {
  return [
    // CLM-0909 — James Whitfield · Reprocess
    {
      id: 'CLM-0909',
      kind: 'underpaid',
      patient: 'James Whitfield',
      firstName: 'James',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      memberId: 'W281-77-1904',
      serviceDate: 'Oct 2, 2026',
      provider: 'Dr. Emily Carter',
      procedures: [{ code: 'D2740', description: 'Crown, porcelain/ceramic', tooth: '#14', fee: 1295 }],
      paidDate: 'Oct 2, 2026',
      paid: 408,
      // Aetna's processing mistake: the patient can't be billed for it (rcm-30 §4).
      canBillPatient: false,
      planRemaining: 1140,
      table: [{ code: 'D2740', billed: 1295, yourFee: 1020, feeUsed: 680, planPaysPct: 60, paid: 408 }],
      tableFooter:
        'Expected = your Aetna fee × the 60% James’s plan pays for crowns. His plan has $1,140 left this year, so the annual maximum didn’t limit it.',
      answer: 'reprocess',
      why: {
        eyebrow: 'Why it’s short',
        headline: 'Aetna paid at its out-of-network fee',
        body:
          'Dr. Carter is in Aetna’s network, so this claim should have paid at his contracted fee of $1,020, not the $680 out-of-network amount. Aetna’s own provider directory lists him as in-network.',
        sources: [
          { id: 'eob-0909', label: 'Aetna EOB, Oct 2' },
          { id: 'contract-0909', label: 'Dr. Carter’s Aetna contract' },
        ],
      },
      recommendationHeader: 'ELVA recommends asking Aetna to reprocess',
      reprocess: {
        deadline: { date: '2027-03-31', basis: 'Aetna allows 180 days', verb: 'Ask' },
        body:
          'This is a processing mistake, not a disagreement, so it doesn’t need an appeal. Aetna handles it as a reconsideration by phone or on its provider website, usually within 3 to 5 business days.',
        elvaWillDo:
          'ELVA calls Aetna’s provider line, asks for the claim to be reprocessed at Dr. Carter’s network fee, and saves the reference number here. If Aetna says no, ELVA prepares an appeal.',
        phone: '1-800-451-7715',
      },
      history: ['Aetna paid Oct 2 (electronic payment)', 'ELVA checked the fee Oct 2'],
    },

    // CLM-0921 — Ben Adler · Reprocess
    {
      id: 'CLM-0921',
      kind: 'underpaid',
      patient: 'Ben Adler',
      firstName: 'Ben',
      payer: { name: 'Delta Dental PPO', short: 'Delta' },
      memberId: '0917-31-7702',
      serviceDate: 'Oct 8, 2026',
      provider: 'Dr. Olivia Brooks',
      procedures: [{ code: 'D0210', description: 'X-rays, complete series', tooth: null, fee: 125 }],
      paidDate: 'Oct 8, 2026',
      paid: 62,
      // Delta's processing mistake: the patient can't be billed for it (rcm-30 §4).
      canBillPatient: false,
      planRemaining: 1500,
      table: [{ code: 'D0210', billed: 125, yourFee: 100, feeUsed: 62, feeUsedNote: '(bitewing fee)', planPaysPct: 100, paid: 62 }],
      tableFooter:
        'Expected = your Delta fee × the 100% Ben’s plan pays. His plan has $1,500 left this year, so the annual maximum didn’t limit it.',
      answer: 'reprocess',
      why: {
        eyebrow: 'Why it’s short',
        headline: 'Delta paid it as bitewings',
        body:
          'The claim was for a complete X-ray series (D0210), but Delta paid the bitewing fee (D0274). The codes don’t match.',
        sources: [{ id: 'eob-0921', label: 'Delta EOB, Oct 8' }],
      },
      recommendationHeader: 'ELVA recommends asking Delta to reprocess',
      reprocess: {
        deadline: { date: '2027-04-06', basis: 'Delta allows 180 days', verb: 'Ask' },
        body:
          'This is a processing mistake, not a disagreement, so it doesn’t need an appeal. Delta handles it as a reconsideration by phone or on its provider website, usually within 3 to 5 business days.',
        elvaWillDo:
          'ELVA calls Delta’s provider line, asks for the claim to be reprocessed as a complete X-ray series, and saves the reference number here. If Delta says no, ELVA prepares an appeal.',
        phone: '1-800-521-2651',
      },
      history: ['Delta paid Oct 8 (electronic payment)', 'ELVA checked the code Oct 8'],
    },

    // CLM-0917 — Karen Liu · Appeal
    {
      id: 'CLM-0917',
      kind: 'underpaid',
      patient: 'Karen Liu',
      firstName: 'Karen',
      payer: { name: 'Guardian', short: 'Guardian' },
      memberId: '6120-771-09',
      // Contractual underpayment, not a patient balance: the Guardian contract doesn't allow billing Karen (D157).
      canBillPatient: false,
      serviceDate: 'Sep 25, 2026',
      provider: 'Dr. Emily Carter',
      procedures: [{ code: 'D2393', description: 'Composite, three surfaces, posterior', tooth: '#30', fee: 264 }],
      paidDate: 'Sep 25, 2026',
      paid: 94,
      planRemaining: 820,
      table: [{ code: 'D2393', billed: 264, yourFee: 235, feeUsed: 117.5, feeUsedNote: '(amalgam fee)', planPaysPct: 80, paid: 94 }],
      tableFooter:
        'Expected = your Guardian fee × the 80% Karen’s plan pays for posterior composites. Her plan has $820 left this year, so the annual maximum didn’t limit it.',
      answer: 'appeal',
      why: {
        eyebrow: 'Why it’s short',
        headline: 'Guardian paid it as a silver filling',
        body:
          'Guardian paid the amalgam allowance, but Karen’s plan covers composite fillings on back teeth. Tooth #30 is a molar, and the restoration was a three-surface composite.',
        sources: [
          { id: 'eob-0917', label: 'Guardian EOB, Sep 25' },
          { id: 'plan-0917', label: 'Karen’s plan — posterior composites' },
        ],
      },
      deadline: { date: '2027-03-24', basis: 'Guardian allows 180 days from its payment', verb: 'Appeal' },
      appealKind: 'billing',
      recommendationHeader: 'ELVA recommends an appeal',
      recommendationBody:
        'Karen’s plan covers posterior composites for in-network dentists. The EOB shows Guardian applied the amalgam allowance by mistake, and the enclosed plan summary shows the composite benefit.',
      recommendationSources: [{ id: 'plan-0917', label: 'Karen’s plan summary' }],
      packet: [
        { n: 1, name: 'Appeal letter', origin: 'Written by ELVA · Dr. Carter signs it', pages: 1, isLetter: true, needsSignature: true },
        { n: 2, name: 'Guardian’s explanation of benefits, Sep 25', origin: 'From Guardian’s electronic payment', pages: 1 },
        { n: 3, name: 'The original claim', origin: 'ADA claim form, as sent on Sep 26', pages: 1 },
        { n: 4, name: 'Karen’s plan summary — posterior composites', origin: 'From the benefits on file', pages: 1 },
      ],
      letter: {
        text:
          'To the Appeals Team,\n\nWe are appealing Guardian’s payment of September 25, 2026 on claim CLM-0917 for Ms. Liu. The three-surface composite on tooth #30 (D2393) was paid at the amalgam allowance of $117.50. Ms. Liu’s plan covers posterior composite restorations at 80% of the contracted fee. Tooth #30 is a molar and the restoration was a direct resin composite, as the enclosed claim and clinical record show. We ask Guardian to reprocess this claim under the posterior composite benefit.',
        basis: 'the EOB and Karen’s plan summary',
      },
      history: ['Guardian paid Sep 25 (electronic payment)', 'ELVA prepared the appeal Sep 26'],
    },
  ];
}

/**
 * The full §12 "Appeals sent" list: 8 open rows + 1 closed (board 6's state,
 * after Linda and Michael have been sent). The store decides which rows to
 * include per scenario (the default drops Linda + Michael, who are still in
 * Denied).
 */
export function buildAppealsSent(): AppealSent[] {
  return [
    {
      id: 'CLM-0611',
      patient: 'Rachel Stone',
      payerShort: 'Aetna',
      codes: 'D2950',
      billed: 285,
      sentDate: '2026-07-28',
      sentDisplay: 'Jul 28',
      sentHow: 'Certified mail',
      status: 'denied_again',
      happening:
        'Aetna kept its denial on Sep 20. Aetna gives dentists one level of appeal, so this one is finished.',
      action: 'decide',
      decideTitle: 'Aetna kept its denial — what now?',
      hasAnotherLevel: false,
      billAmount: 285,
    },
    {
      id: 'CLM-0920',
      patient: 'Daniel Kim',
      payerShort: 'Guardian',
      codes: 'D4342',
      billed: 210,
      sentDate: '2026-10-13',
      sentDisplay: 'Oct 13',
      sentHow: 'Fax',
      status: 'faxed',
      happening:
        'All 7 pages went through to Guardian’s PPO appeals fax. Guardian hasn’t confirmed it has it yet; it says faxes take up to 2 business days, so ELVA calls Guardian tomorrow to check.',
    },
    {
      id: 'CLM-0827',
      patient: 'Michael Torres',
      payerShort: 'Cigna',
      codes: 'D2740',
      billed: 1295,
      sentDate: '2026-10-15',
      sentDisplay: 'Oct 15',
      sentHow: 'Certified mail, return receipt',
      status: 'sent',
      happening: 'Being printed. It goes to USPS by Oct 19; ELVA tracks it to Cigna and keeps the receipt.',
    },
    {
      id: 'CLM-0811',
      patient: 'Helen Ortiz',
      payerShort: 'Aetna',
      codes: 'D4910',
      billed: 160,
      sentDate: '2026-10-07',
      sentDisplay: 'Oct 7',
      sentHow: 'Fax, with Helen’s form',
      status: 'waiting',
      happening:
        'A patient’s appeal: Aetna needed Helen to name the practice to act for her. ELVA texted her Aetna’s form; she signed it on her phone Oct 6. Aetna confirmed it has it on Oct 9 and answers within 30 days, by Nov 8.',
    },
    {
      id: 'CLM-0904',
      patient: 'Robert Nguyen',
      payerShort: 'Humana',
      codes: 'D0330',
      billed: 140,
      sentDate: '2026-10-13',
      sentDisplay: 'Oct 13',
      sentHow: 'Humana’s secure email',
      status: 'waiting',
      happening:
        'A reconsideration, not an appeal: Humana takes these by secure email after a claim question goes unanswered. Humana confirmed it has it on Oct 14 and answers within 30 days, by Nov 13.',
    },
    {
      id: 'CLM-0702',
      patient: 'Grace Hall',
      payerShort: 'Aetna',
      codes: 'D2740',
      billed: 1295,
      sentDate: '2026-09-22',
      sentDisplay: 'Sep 22',
      sentHow: 'Certified mail, return receipt',
      status: 'waiting',
      happening: 'Delivered Sep 26; Aetna signed the return receipt. Aetna answers within 60 days, by Nov 25.',
      trackingLink: true,
    },
    {
      id: 'CLM-0826',
      patient: 'Priya Shah',
      payerShort: 'Delta',
      codes: 'D2392',
      billed: 245,
      sentDate: '2026-10-09',
      sentDisplay: 'Oct 9',
      sentHow: 'Delta’s website',
      status: 'waiting',
      happening:
        'Delta asks for a claim adjustment before a dispute. ELVA asked for one on Oct 1; Delta said no on Oct 8, so ELVA filed the dispute on Delta’s website · reference DD-26-118204. Delta answers within 45 working days, by about Dec 11.',
    },
    {
      id: 'CLM-0818',
      patient: 'Linda Okafor',
      payerShort: 'Aetna',
      codes: 'D2740, D2950',
      billed: 1580,
      sentDate: '2026-10-15',
      sentDisplay: 'Oct 15',
      sentHow: 'Aetna’s website',
      status: 'waiting',
      happening:
        'Filed on Aetna’s dental website · confirmation AD-7731904, so Aetna has it. Aetna answers within 60 days, by Dec 14.',
    },
    {
      id: 'CLM-0610',
      patient: 'Tomás Vega',
      payerShort: 'Cigna',
      codes: 'D4341 ×2',
      billed: 530,
      sentDate: '2026-08-12',
      sentDisplay: 'Aug 12',
      sentHow: 'Certified mail',
      status: 'paid',
      happening: 'Cigna paid $410 on Oct 3. ELVA posted the payment.',
      recoveredAmount: 410,
      closed: true,
      closedNote: 'Tomás Vega · Cigna paid $410 on Oct 3',
    },
  ];
}

/** What words describe a fallback order (§15 "If that fails" column). */
function fallbackWords(order: Channel[]): string {
  const rest = order.slice(1);
  if (rest.length === 0) return '—';
  const labels = rest.map((ch) => {
    const found = CHANNELS_IN_ORDER.find((c) => c.id === ch);
    return found ? found.label : ch;
  });
  if (labels.length === 1) return labels[0] ?? '—';
  return `${labels.slice(0, -1).join(', ')}, then ${labels[labels.length - 1] ?? ''}`;
}

/** Settings › Appeals mock (§15). */
export function buildAppealSettings(): AppealSettings {
  return {
    authorization: { signed: true, by: 'Dr. Emily Carter, owner', date: 'Oct 1, 2026' },
    autopilot: false,
    confirmed: 4,
    billingSigner: 'Dr. Emily Carter',
    dentists: [
      { id: 'carter', name: 'Dr. Emily Carter', onFile: true, adoptedDate: 'Oct 1, 2026' },
      { id: 'brooks', name: 'Dr. Olivia Brooks', onFile: true, adoptedDate: 'Oct 1, 2026' },
    ],
    payers: PAYER_PLAN_RULES.map((rule) => {
      const first = rule.order[0] ?? rule.accepts[0] ?? 'certified_mail';
      return {
        key: rule.key,
        payerPlan: `${rule.payer} ${rule.plan}`,
        accepts: rule.acceptsWords,
        acceptedChannels: rule.accepts,
        first,
        fallback: rule.required ? '—' : fallbackWords(rule.order),
        ...(rule.required ? { required: true } : {}),
        firstStep: rule.firstStep ?? null,
        source: rule.source,
        checkedOn: rule.checkedOn,
      };
    }),
  };
}
